import {
  Component,
  DOCUMENT,
  ElementRef,
  inject,
  OnInit,
  PLATFORM_ID,
  Renderer2,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { isPlatformServer } from '@angular/common';
import { TranslateService, TranslationObject } from '@ngx-translate/core';
import htmlTagHelper from './utils/html-tags-helper';
import {
  EXTERNAL_TAGS_TOKEN,
  PAGE_TITLE_TOKEN,
} from '../backend/utils/provide-tokens';
import { Title } from '@angular/platform-browser';
import { AssetsUrlPipe } from './pipes/assets-url.pipe';
import { RequestDataService } from './services/request-data.service';
import ptTranslations from '../assets/i18n/pt.json';
import esTranslations from '../assets/i18n/es.json';
import enTranslations from '../assets/i18n/en.json';
import esARTranslations from '../assets/i18n/es-AR.json';

const GlobalTranslationsMap: Record<string, TranslationObject> = {
  pt: ptTranslations as TranslationObject,
  es: esTranslations as TranslationObject,
  en: enTranslations as TranslationObject,
};

const RegionalTranslationsMap: Record<string, TranslationObject> = {
  'es-AR': esARTranslations as TranslationObject,
};

@Component({
  imports: [RouterModule],
  selector: 'al-app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  protected title = 'account-linker';

  private elementRef = inject(ElementRef);
  private renderer = inject(Renderer2);
  private titleService = inject(Title);
  private assetsUrlPipe = inject(AssetsUrlPipe);
  private requestDataService = inject(RequestDataService);
  private translateService = inject(TranslateService);

  private dom = inject(DOCUMENT);
  private platformId = inject(PLATFORM_ID);
  private pageTitle = inject(PAGE_TITLE_TOKEN, { optional: true });
  private externalTags = inject(EXTERNAL_TAGS_TOKEN, {
    optional: true,
  });

  constructor() {
    const userLocale = this.requestDataService.getLocale();
    this.translateService.addLangs([userLocale.canonical]);
    this.translateService.use(userLocale.canonical);
    this.translateService.setFallbackLang(userLocale.language);
    this.translateService.setTranslation(
      userLocale.language,
      GlobalTranslationsMap[userLocale.language]
    );
    const regionalism = RegionalTranslationsMap[userLocale.canonical];
    if (regionalism) {
      this.translateService.setTranslation(userLocale.canonical, regionalism);
    }
  }

  ngOnInit(): void {
    if (isPlatformServer(this.platformId)) {
      const userLocale = this.requestDataService.getLocale();
      // Set the "lang" attribute with the current locale on the <html> element
      this.renderer.setAttribute(
        this.dom.documentElement,
        'lang',
        userLocale.canonical
      );

      this.dom.head.insertAdjacentHTML(
        'beforeend',
        `
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="${this.requestDataService.getPosixLocale()}" />
      `
      );

      if (this.pageTitle) {
        this.titleService.setTitle(this.pageTitle);
        this.dom.head.insertAdjacentHTML(
          'beforeend',
          `<meta property="og:title" content="${this.pageTitle}" />`
        );
      }

      // In webview we have to add this class to the body
      if (this.requestDataService.isWebView()) {
        this.renderer.addClass(this.dom.body, 'not-selectable');
      }

      if (this.externalTags && this.externalTags.dreck) {
        this.dom.head.insertAdjacentHTML(
          'afterbegin',
          `
          <script>
            var updateDreckData = function(){
              if (window.dreckInfoData && window.dreckInfoData.updateDreckData) {
                window.dreckInfoData.updateDreckData({pageview_id: "${this.requestDataService.getPageviewId()}"});
              }
            };
            updateDreckData();
          </script>
        `
        );
        this.dom.head.insertAdjacentHTML(
          'afterbegin',
          this.externalTags.dreck.head
        );
        this.elementRef.nativeElement.insertAdjacentHTML(
          'beforebegin',
          this.externalTags.dreck.bodyTop
        );
        this.elementRef.nativeElement.insertAdjacentHTML(
          'afterend',
          this.externalTags.dreck.bodyBottom
        );
      }

      if (this.externalTags && this.externalTags.shifu) {
        this.externalTags.shifu.css.forEach((value: string) => {
          htmlTagHelper.addPreloadLink(this.dom, 'style', value, false);
          htmlTagHelper.addCSSLink(this.renderer, this.dom, value);
        });
      }

      if (this.externalTags && this.externalTags.shifu) {
        this.elementRef.nativeElement.insertAdjacentHTML(
          'beforebegin',
          this.externalTags.shifu.header
        );

        this.elementRef.nativeElement.insertAdjacentHTML(
          'afterend',
          this.externalTags.shifu.footer
        );

        this.externalTags.shifu.js.forEach((value: string) => {
          htmlTagHelper.addPreloadLink(this.dom, 'script', value);
          htmlTagHelper.addScript(this.renderer, this.dom.body, value, true);
        });
      }

      htmlTagHelper.addCSSLink(
        this.renderer,
        this.dom,
        this.assetsUrlPipe.transform('favicon.ico'),
        'icon',
        'image/x-icon'
      );
    }
  }
}
